/**
 * @fileoverview List caller rules on the Admin Phone tab: calls to the main number from a chosen caller ring one person or a group. `CallerRuleEditor.js` adds and edits rules in a dialog. The backend (`kc-internal-backend/functions/phone_caller_rules.py`) owns number checks and which people have a live phone; the phone-system reads the saved rows. A saved row does not prove the phone server has reloaded.
 */

import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';

import api from '../../api';
import ToastContext from '../../ToastContext';
import { AppButton, AppIcon, AppTextInput } from '../shared';
import { AdminCard } from './AdminChrome';
import CallerRuleEditor from './CallerRuleEditor';
import { avatarHue, formatPhone, initials, ruleHistory } from './callerRuleFormat';

// The search box appears once the list is long enough to need it.
const SEARCH_FROM = 5;

// A wrapped caller name never fills its box, so CSS alone leaves the arrow
// short of the text. Size the shared caller column to the longest line any
// name or number actually uses; PhoneAdmin.css keeps fit-content as the fallback.
function fitCallerColumn(list) {
  if (!list || typeof Range === 'undefined' || !Range.prototype.getClientRects) return;
  list.style.removeProperty('--caller-column');
  let widest = 0;
  list.querySelectorAll('.caller-rule__caller').forEach((caller) => {
    caller.querySelectorAll('strong, span').forEach((text) => {
      const range = document.createRange();
      range.selectNodeContents(text);
      const left = caller.getBoundingClientRect().left;
      [...range.getClientRects()].forEach((line) => { widest = Math.max(widest, line.right - left); });
    });
  });
  if (widest > 0) list.style.setProperty('--caller-column', `${Math.ceil(widest)}px`);
}


function CallerRules() {
  const showToast = useContext(ToastContext);
  const [rules, setRules] = useState(null);
  const [people, setPeople] = useState([]);
  const [editing, setEditing] = useState(null); // null, 'new', or the rule being edited
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  // Shown inside the dialog: a toast would sit under the dialog's backdrop.
  const [editorError, setEditorError] = useState(null);

  useEffect(() => {
    let active = true;
    api.get('/admin/caller-rules')
      .then(({ data }) => {
        if (!active) return;
        setRules(data.caller_rules);
        setPeople(data.people);
      })
      .catch(() => active && showToast({ severity: 'error', summary: 'Error', detail: 'Caller rules could not be loaded' }));
    return () => { active = false; };
  }, [showToast]);

  const errorText = (error) => error?.response?.data?.error || 'Something went wrong. Try again.';

  const openEditor = (rule) => {
    setEditorError(null);
    setEditing(rule);
  };

  const save = async (body) => {
    setBusy(true);
    setEditorError(null);
    try {
      if (editing === 'new') {
        const { data } = await api.post('/admin/caller-rules', body);
        setRules((current) => [data.caller_rule, ...current]);
      } else {
        const { data } = await api.put(`/admin/caller-rules/${editing.id}`, body);
        setRules((current) => current.map((rule) => (rule.id === editing.id ? data.caller_rule : rule)));
      }
      setEditing(null);
    } catch (error) {
      setEditorError(`Could not save: ${errorText(error)}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the caller rule for ${formatPhone(editing.phone)}? Calls from this number will go to the main menu again.`)) return;
    setBusy(true);
    setEditorError(null);
    try {
      await api.delete(`/admin/caller-rules/${editing.id}`);
      setRules((current) => current.filter((rule) => rule.id !== editing.id));
      setEditing(null);
    } catch (error) {
      setEditorError(`Could not delete: ${errorText(error)}`);
    } finally {
      setBusy(false);
    }
  };

  // Stable so the dialog's Escape listener isn't rebound on every render.
  const close = useCallback(() => setEditing((current) => (busy ? current : null)), [busy]);

  const query = search.trim().toLowerCase();
  const queryDigits = query.replace(/\D/g, '');
  const shown = (rules || []).filter((rule) => !query
    || (rule.label || '').toLowerCase().includes(query)
    || (queryDigits && rule.phone.includes(queryDigits))
    || rule.recipients.some((person) => (person.name || '').toLowerCase().includes(query)));
  const listRef = useRef(null);
  const shownKey = shown.map((rule) => `${rule.id}:${rule.label}`).join('|');
  useLayoutEffect(() => {
    const fit = () => fitCallerColumn(listRef.current);
    fit();
    document.fonts?.ready.then(fit);
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [shownKey]);

  return (
    <AdminCard
      title="Caller rules"
      description="These callers skip the main menu and ring the people listed. If they dial a listed person's direct line, only that person rings, with the same ring option."
      actionsAlign="center"
      actions={<AppButton icon="pi pi-plus" label="Add caller rule" disabled={rules === null} onClick={() => openEditor('new')} />}>
      {rules?.length > SEARCH_FROM ? (
        <AppTextInput aria-label="Search caller rules" placeholder="Search by name or number…" value={search}
          onChange={(event) => setSearch(event.target.value)} />
      ) : null}

      {rules?.length === 0 ? <p className="caller-rules-empty">No caller rules yet. Add one to send a caller straight to the right people.</p> : null}
      {rules?.length && !shown.length ? <p className="caller-rules-empty">No caller rules match “{search}”.</p> : null}
      {shown.length ? (
        <ul ref={listRef} className="caller-rules" aria-label="Caller rules">
          {shown.map((rule) => {
            const gone = rule.recipients.filter((person) => person.inactive);
            const first = rule.recipients[0];
            return (
              <li key={rule.id} className="caller-rule">
                <span className="caller-rule__caller">
                  <strong>{rule.label || 'Unnamed caller'}</strong>
                  <span>{formatPhone(rule.phone)}</span>
                </span>
                <AppIcon icon="pi pi-arrow-right" className="caller-rule__to" aria-hidden="true" />
                <span className="caller-rule__route">
                  <span className="caller-rule__people">
                    {rule.recipients.map((person) => (
                      <span key={person.email} className={`caller-rule__person${person.inactive ? ' is-gone' : ''}`}>
                        <span className="caller-rule__avatar" style={{ '--avatar-hue': avatarHue(person.email) }} aria-hidden="true">
                          {initials(person.name || person.email)}
                        </span>
                        {person.name || person.email}
                      </span>
                    ))}
                  </span>
                  {(rule.recipients.length > 1 && first?.name) || rule.ring_outside_hours ? (
                    <span className="caller-rule__notes">
                      {rule.recipients.length > 1 && first?.name ? (
                        <span><AppIcon icon="pi pi-inbox" aria-hidden="true" />Missed calls go to {first.name}'s voicemail</span>
                      ) : null}
                      {rule.ring_outside_hours ? (
                        <span><AppIcon icon="pi pi-moon" aria-hidden="true" />Rings outside working hours</span>
                      ) : null}
                    </span>
                  ) : null}
                  {gone.length ? (
                    <span className="caller-rule__warning">
                      <AppIcon icon="pi pi-exclamation-triangle" aria-hidden="true" />
                      {gone.map((person) => person.name || person.email).join(', ')} no longer has an active phone and won't ring
                    </span>
                  ) : null}
                  <span className="caller-rule__history">{ruleHistory(rule)}</span>
                </span>
                <AppButton variant="secondary" label="Edit" aria-label={`Edit caller rule for ${formatPhone(rule.phone)}`}
                  disabled={busy} onClick={() => openEditor(rule)} />
              </li>
            );
          })}
        </ul>
      ) : null}

      {editing ? (
        <CallerRuleEditor
          rule={editing === 'new' ? null : editing}
          people={people}
          busy={busy}
          error={editorError}
          onSave={save}
          onDelete={remove}
          onClose={close} />
      ) : null}
    </AdminCard>
  );
}

export default CallerRules;
